import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { useTranslations } from 'gt-react'
import { ArrowRight, Check } from 'lucide-react'
import { BlurText } from '@/components/reactbits'
import { cn } from '@/lib/utils'

const ease = [0.22, 1, 0.36, 1] as const

export function Hero() {
  const t = useTranslations('hero')

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#08080c]">
      {/* Dot grid — subtle texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.25) 1px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* Primary gradient wash — top-left, subtle */}
      <div
        className="pointer-events-none absolute -top-32 -left-32 h-[500px] w-[500px] rounded-full opacity-[0.15] blur-[100px]"
        style={{ background: '#5865F2' }}
      />
      {/* Secondary wash — bottom-right, very subtle, same color */}
      <div
        className="pointer-events-none absolute -bottom-48 -right-48 h-[400px] w-[400px] rounded-full opacity-[0.06] blur-[120px]"
        style={{ background: '#5865F2' }}
      />

      <div className="relative mx-auto w-full max-w-7xl px-4 py-32 sm:px-6 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-[1fr,1.15fr] lg:items-center">
          {/* Left: copy */}
          <div>
            {/* Eyebrow */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3.5 py-1.5 text-xs font-medium text-white/50">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                {t('eyebrow')}
              </span>
            </motion.div>

            {/* Headline with BlurText animation */}
            <h1 className="mt-7 text-[2.5rem] font-bold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-[3.5rem]">
              <BlurText
                text="The ERP platform"
                delay={60}
                animateBy="words"
                stepDuration={0.35}
                className="block"
              />
              <span className="block mt-1">
                <BlurText
                  text="built for "
                  delay={60}
                  animateBy="words"
                  stepDuration={0.35}
                />
                <BlurText
                  text="your business"
                  delay={60}
                  animateBy="words"
                  stepDuration={0.35}
                  className="text-primary font-extrabold"
                />
              </span>
            </h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5, ease }}
              className="mt-5 max-w-md text-[0.95rem] leading-relaxed text-white/40 sm:text-base"
            >
              {t('subtitle')}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6, ease }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/sign-up"
                className={cn(
                  'group inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold',
                  'bg-white text-[#08080c]',
                  'transition-all duration-300',
                  'hover:shadow-[0_0_28px_rgba(88,101,242,0.25)]',
                )}
              >
                {t('cta_primary')}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/pricing"
                className="text-sm font-medium text-white/35 transition-colors duration-200 hover:text-white/60"
              >
                {t('cta_secondary')} →
              </Link>
            </motion.div>

            {/* Trust signals */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8, ease }}
              className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-white/25"
            >
              {[t('trust_note'), 'Multi-tenant', 'RBAC built-in', 'Your data, your server'].map(
                (item) => (
                  <span key={item} className="flex items-center gap-1.5">
                    <Check className="h-3 w-3 text-emerald-400/50" />
                    {item}
                  </span>
                ),
              )}
            </motion.div>
          </div>

          {/* Right: product preview */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4, ease }}
            className="hidden lg:block"
          >
            <div className="relative">
              {/* Glow */}
              <div
                className="absolute -inset-6 rounded-2xl opacity-20 blur-3xl"
                style={{ background: '#5865F2' }}
              />
              <ProductPreview />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

function ProductPreview() {
  return (
    <div className="relative overflow-hidden rounded-xl border border-white/[0.06] bg-[#0e0e14] shadow-2xl shadow-black/50">
      {/* Title bar */}
      <div className="flex items-center gap-2 border-b border-white/[0.05] px-4 py-2.5">
        <div className="flex gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
        </div>
        <div className="ml-3 h-4 w-36 rounded-sm bg-white/[0.04]" />
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-44 shrink-0 border-r border-white/[0.05] p-2.5 space-y-0.5">
          {['Dashboard', 'Employees', 'Invoices', 'Inventory', 'Projects'].map((item, i) => (
            <div
              key={item}
              className={cn(
                'rounded-md px-2.5 py-1.5 text-[11px]',
                i === 0
                  ? 'bg-white/[0.06] text-white/60 font-medium'
                  : 'text-white/20',
              )}
            >
              {item}
            </div>
          ))}
          <div className="pt-2 mt-2 border-t border-white/[0.04]">
            <div className="text-[9px] font-medium text-white/10 uppercase tracking-wider px-2.5 mb-1">
              Custom
            </div>
            {['Food Safety', 'Logistics'].map((item) => (
              <div key={item} className="rounded-md px-2.5 py-1.5 text-[11px] text-white/20">
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Main */}
        <div className="flex-1 p-3.5 space-y-3">
          <div className="grid grid-cols-3 gap-2.5">
            {[
              { label: 'Active employees', value: '142', change: '+12%' },
              { label: 'Open invoices', value: '€38.4k', change: '-8%' },
              { label: 'Tasks due today', value: '7', change: '' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-white/[0.05] bg-white/[0.015] p-2.5"
              >
                <div className="text-[9px] text-white/20">{stat.label}</div>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="text-base font-semibold text-white/55 tabular-nums">
                    {stat.value}
                  </span>
                  {stat.change && (
                    <span
                      className={cn(
                        'text-[9px] font-medium',
                        stat.change.startsWith('+')
                          ? 'text-emerald-400/50'
                          : 'text-rose-400/50',
                      )}
                    >
                      {stat.change}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="rounded-lg border border-white/[0.05] overflow-hidden">
            <div className="bg-white/[0.015] px-2.5 py-1.5 text-[9px] font-medium text-white/20 flex">
              <span className="flex-1">Name</span>
              <span className="w-20">Department</span>
              <span className="w-16 text-right">Status</span>
            </div>
            {[
              { name: 'Sarah Chen', dept: 'Engineering', status: 'Active' },
              { name: 'Marcus Veil', dept: 'Finance', status: 'Active' },
              { name: 'Priya Sharma', dept: 'HR', status: 'On leave' },
              { name: 'Tom Bakker', dept: 'Operations', status: 'Active' },
            ].map((row) => (
              <div
                key={row.name}
                className="flex items-center px-2.5 py-1.5 border-t border-white/[0.03] text-[11px]"
              >
                <span className="flex-1 text-white/40">{row.name}</span>
                <span className="w-20 text-white/18">{row.dept}</span>
                <span className="w-16 text-right">
                  <span
                    className={cn(
                      'inline-block rounded-lg px-1.5 py-0.5 text-[9px]',
                      row.status === 'Active'
                        ? 'bg-emerald-500/8 text-emerald-400/50'
                        : 'bg-amber-500/8 text-amber-400/50',
                    )}
                  >
                    {row.status}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
